import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { salesOrderAPI } from "../../services/api";
import { useNotification } from '../../hooks/useNotification';

const SalesOrderList = () => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useNotification();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [itemsModalOpen, setItemsModalOpen] = useState(false);
  const [itemsModalOrder, setItemsModalOrder] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [printingOrderId, setPrintingOrderId] = useState(null);

  useEffect(() => {
    loadOrders();
  }, [statusFilter, paymentStatusFilter]);

  const loadOrders = async () => {
    try {
      // Request items relation if backend supports 'include' param. This helps ensure
      // the list endpoint returns nested items after an update.
      const params = statusFilter ? { status: statusFilter } : {};
      // ask backend to include items and nested inventory_item when possible
      params.include = params.include || 'items.inventory_item';
      const response = await salesOrderAPI.getAll(params);
      // Debug: log full response to quickly inspect backend response shape
      // eslint-disable-next-line no-console
      console.debug('GET /sales-orders response:', response.data);
      setOrders(response.data.data || response.data);
    } catch (error) {
      showError(t("messages.error"));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("messages.confirmDelete"))) return;
    try {
      await salesOrderAPI.delete(id);
      loadOrders();
    } catch (error) {
      showError(t("messages.error"));
    }
  };

  const handleCancel = async (id, orderNumber) => {
    const confirmCancel = window.confirm(
      `Are you sure you want to cancel order ${orderNumber}?\n\n` +
      `This will:\n` +
      `• Cancel all linked lab tasks\n` +
      `• Restore inventory\n\n` +
      `Continue?`
    );

    if (!confirmCancel) return;

    try {
      const response = await fetch(`/api/sales-orders/${id}/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        showSuccess(
          `Order cancelled successfully. ${data.cancelled_lab_tasks || 0} lab tasks cancelled, inventory restored.`
        );
        loadOrders();
      } else {
        const errorData = await response.json();
        showError(errorData.message || t("messages.error"));
      }
    } catch (error) {
      showError(t("messages.error"));
      console.error('Cancel error:', error);
    }
  };

  const handleDirectPrint = async (order) => {
    setPrintingOrderId(order.id);
    setOpenDropdown(null);
    try {
      // Create invisible iframe for printing
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = `/invoice/${order.id}?print=true`;
      document.body.appendChild(iframe);

      // Remove iframe after printing
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 4000);

      showSuccess('Invoice printing started');
    } catch (error) {
      showError('Failed to start printing');
      console.error('Print error:', error);
    } finally {
      setPrintingOrderId(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Delivered":
        return { bg: "bg-green-500", text: "text-green-700" };
      case "Ready":
        return { bg: "bg-blue-500", text: "text-blue-700" };
      case "In Production":
        return { bg: "bg-yellow-500", text: "text-yellow-700" };
      case "Pending":
        return { bg: "bg-gray-400", text: "text-gray-700" };
      case "Cancelled":
        return { bg: "bg-red-500", text: "text-red-700" };
      default:
        return { bg: "bg-gray-400", text: "text-gray-700" };
    }
  };

  const getPaymentStatusColor = (paymentStatus) => {
    switch (paymentStatus) {
      case "paid":
        return {
          bg: "bg-green-100",
          text: "text-green-700",
          border: "border-green-300",
          icon: "text-green-600",
        };
      case "partial":
        return {
          bg: "bg-yellow-100",
          text: "text-yellow-700",
          border: "border-yellow-300",
          icon: "text-yellow-600",
        };
      case "unpaid":
        return {
          bg: "bg-red-100",
          text: "text-red-700",
          border: "border-red-300",
          icon: "text-red-600",
        };
      default:
        return {
          bg: "bg-gray-100",
          text: "text-gray-700",
          border: "border-gray-300",
          icon: "text-gray-600",
        };
    }
  };

  const calculateRemainingAmount = (order) => {
    const total = parseFloat(order.total_price || 0);
    const paid = parseFloat(order.amount_paid || 0);
    return (total - paid).toFixed(2);
  };

  const filteredOrders = orders.filter((order) => {
    if (paymentStatusFilter && order.payment_status !== paymentStatusFilter) {
      return false;
    }
    return true;
  });

  const renderOrderItems = (order) => {
    // Support multiple possible backend shapes. Prefer order.items but fallback to other names.
    const items = order.items || order.sales_order_items || order.order_items || [];
    if (items && items.length > 0) {
      return items.map((item, index) => {
        // inventory may be nested under different keys depending on backend
        const inv = item.inventory_item || item.inventory || item.item || item.product || null;
        const name = inv?.item_name || inv?.name || item.item_name || item.name || t('common.unknown');
        const deleted = inv?.deleted_at || item.deleted_at;
        return (
          <span key={index} className="text-sm text-gray-600">
            {name}
            {deleted && (
              <span className="text-xs text-red-500 ml-1">(Deleted)</span>
            )}
            {index < items.length - 1 && ", "}
          </span>
        );
      });
    }
    return <span className="text-sm text-gray-400">{t('salesOrders.noItems')}</span>;
  };

  const getTotalQuantity = (order) => {
    const items = order.items || order.sales_order_items || order.order_items || [];
    if (items && items.length > 0) {
      const grouped = {};
      items.forEach((item) => {
        const inv = item.inventory_item || item.inventory || item.item || null;
        const unit = inv?.unit || item.unit || "";
        const qty = parseFloat(item.quantity || item.qty || item.quantity_ordered || 0);
        if (!isNaN(qty)) {
          grouped[unit] = (grouped[unit] || 0) + qty;
        }
      });
      const parts = Object.entries(grouped)
        .filter(([unit]) => unit && unit.length > 0)
        .map(([unit, sum]) => `${sum.toFixed(2)} ${unit}`);
      if (parts.length === 0) return "-";
      return parts.join(", ");
    }
    return "-";
  };

  const openItemsModal = (order) => {
    setItemsModalOrder(order);
    setItemsModalOpen(true);
  };

  const closeItemsModal = () => {
    setItemsModalOpen(false);
    setItemsModalOrder(null);
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredOrders.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            {t("salesOrders.title")}
          </h2>
          <p className="text-gray-600 mt-1">{t("salesOrders.subtitle")}</p>
        </div>
        <Link
          to="/sales-orders/new"
          className="flex items-center px-4 py-2 bg-gradient-to-r bg-gray-900 text-white rounded-lg hover:bg-yellow-500  transition-all duration-200 shadow-lg hover:shadow-xl"
        >
          <svg
            className="h-5 w-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          {t("salesOrders.addOrder")}
        </Link>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="mb-6 flex gap-4">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">{t('salesOrders.allOrderStatus')}</option>
            <option value="Pending">{t("salesOrders.statuses.Pending")}</option>
            <option value="In Production">
              {t("salesOrders.statuses.In Production")}
            </option>
            <option value="Ready">{t("salesOrders.statuses.Ready")}</option>
            <option value="Delivered">
              {t("salesOrders.statuses.Delivered")}
            </option>
            <option value="Cancelled">
              {t("salesOrders.statuses.Cancelled")}
            </option>
          </select>

          <select
            value={paymentStatusFilter}
            onChange={(e) => setPaymentStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            <option value="">{t('salesOrders.allPaymentStatus')}</option>
            <option value="paid">
              {t("salesOrders.paymentStatuses.paid")}
            </option>
            <option value="partial">
              {t("salesOrders.paymentStatuses.partial")}
            </option>
            <option value="unpaid">
              {t("salesOrders.paymentStatuses.unpaid")}
            </option>
          </select>
        </div>

        {/* Modern Card Layout */}
        <div className="space-y-3">
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50 rounded-lg text-xs font-medium text-gray-500 uppercase tracking-wider">
            <div className="col-span-2">{t("common.status")}</div>
            <div className="col-span-2">{t("salesOrders.customer")}</div>
            <div className="col-span-2">{t("salesOrders.orderNumber")}</div>
            <div className="col-span-2">{t("salesOrders.paymentStatus")}</div>
            <div className="col-span-2">{t("salesOrders.totalPrice")}</div>
            <div className="col-span-1">{t("salesOrders.orderDate")}</div>
            <div className="col-span-1 text-right">{t("common.actions")}</div>
          </div>

          {/* Card Items */}
          {currentItems.map((order) => {
            const statusColor = getStatusColor(order.status);
            const paymentColor = getPaymentStatusColor(order.payment_status);
            const remainingAmount = calculateRemainingAmount(order);

            return (
              <div
                key={order.id}
                className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 border border-gray-100"
              >
                <div className="grid grid-cols-12 gap-4 px-6 py-4 items-center">
                  {/* Status */}
                  <div className="col-span-2 flex items-center gap-2">
                    <div
                      className={`w-3 h-3 rounded-full ${statusColor.bg}`}
                    ></div>
                    <span className={`text-sm font-medium ${statusColor.text}`}>
                      {t(`salesOrders.statuses.${order.status}`)}
                    </span>
                  </div>

                  {/* Customer & Items */}
                  <div className="col-span-2">
                    <div className="text-sm font-semibold text-gray-900">
                      {order.customer?.name || "N/A"}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5 truncate">
                      {renderOrderItems(order)}
                    </div>
                  </div>

                  {/* Order Number */}
                  <div className="col-span-2">
                    <button
                      type="button"
                      onClick={() => openItemsModal(order)}
                      className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {order.order_number}
                    </button>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {t('accounting.quantity')}: {getTotalQuantity(order)}
                    </div>
                  </div>

                  {/* Payment Status */}
                  <div className="col-span-2">
                    <div
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border ${paymentColor.bg} ${paymentColor.border}`}
                    >
                      {order.payment_status === "paid" && (
                        <svg
                          className={`w-4 h-4 ${paymentColor.icon}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                      {order.payment_status === "partial" && (
                        <svg
                          className={`w-4 h-4 ${paymentColor.icon}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      )}
                      {order.payment_status === "unpaid" && (
                        <svg
                          className={`w-4 h-4 ${paymentColor.icon}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      )}
                      <span
                        className={`text-xs font-semibold ${paymentColor.text}`}
                      >
                        {t(
                          `salesOrders.paymentStatuses.${
                            order.payment_status || "unpaid"
                          }`
                        )}
                      </span>
                    </div>
                    {order.payment_status === "partial" && (
                      <div className="text-xs text-red-600 font-medium mt-1">
                        {t("salesOrders.remaining")}: {t("common.currency")}{" "}
                        {remainingAmount}
                      </div>
                    )}
                  </div>

                  {/* Price */}
                  <div className="col-span-2">
                    <div className="text-sm font-semibold text-gray-900">
                      {t("common.currency")}{" "}
                      {parseFloat(order.total_price || 0).toFixed(2)}
                    </div>
                    {order.amount_paid && (
                      <div className="text-xs text-green-600 mt-0.5">
                        {t("salesOrders.paidLabel")} {t("common.currency")}{" "}
                        {parseFloat(order.amount_paid || 0).toFixed(2)}
                      </div>
                    )}
                  </div>

                  {/* Date */}
                  <div className="col-span-1">
                    <div className="text-xs text-gray-700">
                      {new Date(order.order_date).toLocaleDateString()}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="col-span-1 flex items-center justify-end relative">
                    <button
                      onClick={() => setOpenDropdown(openDropdown === order.id ? null : order.id)}
                      className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title={t("common.actions")}
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                        />
                      </svg>
                    </button>

                    {/* Dropdown Menu */}
                    {openDropdown === order.id && (
                      <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 z-10 top-10">
                        {/* View Section */}
                        <div className="border-b border-gray-100">
                          <Link
                            to={`/sales-orders/${order.id}`}
                            className="flex items-center gap-3 px-4 py-3 hover:bg-blue-50 transition-colors"
                            onClick={() => setOpenDropdown(null)}
                          >
                            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            <span className="text-sm font-medium text-gray-700">{t("common.edit")}</span>
                          </Link>

                          <Link
                            // to={`/sales-orders/invoice/${order.id}`}
                            className="flex items-center gap-3 px-4 py-3 hover:bg-green-50 transition-colors"
                            target="_blank"
                            onClick={() => setOpenDropdown(null)}
                          >
                            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            <span className="text-sm font-medium text-gray-700">{t('salesOrders.viewInvoice')}</span>
                          </Link>
                        </div>

                        {/* Print Section */}
                        <div className="border-b border-gray-100">
                          <button
                            onClick={() => handleDirectPrint(order)}
                            disabled={printingOrderId === order.id}
                            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-purple-50 transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {printingOrderId === order.id ? (
                              <>
                                <svg className="w-5 h-5 text-purple-600 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                <span className="text-sm font-medium text-gray-700">{t('common.printing') || 'Printing...'}</span>
                              </>
                            ) : (
                              <>
                                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                                </svg>
                                <span className="text-sm font-medium text-gray-700">{t('salesOrders.printInvoice')}</span>
                              </>
                            )}
                          </button>
                        </div>

                        {/* Management Section */}
                        <div className="border-b border-gray-100">
                          <Link
                            to={`/returns/create/${order.id}`}
                            className="flex items-center gap-3 px-4 py-3 hover:bg-orange-50 transition-colors"
                            onClick={() => setOpenDropdown(null)}
                          >
                            <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 11l3 3L20 4m0 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-sm font-medium text-gray-700">{t('salesOrders.createReturn')}</span>
                          </Link>

                          {order.status !== 'Cancelled' && order.status !== 'Delivered' && (
                            <button
                              onClick={() => {
                                handleCancel(order.id, order.order_number);
                                setOpenDropdown(null);
                              }}
                              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-amber-50 transition-colors text-left border-t border-gray-100"
                            >
                              <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                              <span className="text-sm font-medium text-gray-700">{t('common.cancel')}</span>
                            </button>
                          )}
                        </div>

                        {/* Delete Section */}
                        <div>
                          <button
                            onClick={() => {
                              handleDelete(order.id);
                              setOpenDropdown(null);
                            }}
                            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-red-50 transition-colors text-left"
                          >
                            <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            <span className="text-sm font-medium text-gray-700">{t("common.delete")}</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200 mt-4">
          <div className="text-sm text-gray-700">
            {t('salesOrders.showLabel')}{" "}
            <span className="font-medium">
              {Math.min(indexOfFirstItem + 1, filteredOrders.length)}
            </span>{" "}
            {t('common.of')} <span className="font-medium">{filteredOrders.length}</span>{" "}
            {t('common.orders')}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common.previous')}
            </button>
            {[...Array(totalPages)].map((_, index) => (
              <button
                key={index + 1}
                onClick={() => paginate(index + 1)}
                className={`px-3 py-1 text-sm font-medium rounded-md ${
                  currentPage === index + 1
                    ? "bg-blue-600 text-white"
                    : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                }`}
              >
                {index + 1}
              </button>
            ))}
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common.next')}
            </button>
            <span className="text-sm text-gray-700 ml-2">
              {indexOfFirstItem + 1} -{" "}
              {Math.min(indexOfLastItem, filteredOrders.length)}
            </span>
          </div>
        </div>
      {itemsModalOpen && itemsModalOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <div className="text-lg font-semibold text-gray-900">
                {t("salesOrders.orderNumber")}: {itemsModalOrder.order_number}
            </div>
            <button
              type="button"
              onClick={closeItemsModal}
              className="p-2 rounded hover:bg-gray-100"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="px-6 py-4 space-y-3">
            {(() => {
              const items = itemsModalOrder.items || itemsModalOrder.sales_order_items || itemsModalOrder.order_items || [];
              const grouped = {};
              items.forEach((item) => {
                const inv = item.inventory_item || item.inventory || item.item || null;
                const unit = inv?.unit || item.unit || "";
                const qty = parseFloat(item.quantity || item.qty || item.quantity_ordered || 0);
                if (!isNaN(qty) && unit) {
                  grouped[unit] = (grouped[unit] || 0) + qty;
                }
              });
              const summary = Object.entries(grouped).map(([unit, sum]) => (
                <span key={unit} className="inline-block mr-2 text-sm text-gray-700">
                  {sum.toFixed(2)} {unit}
                </span>
              ));
              return (
                <div className="">
                  <div className="text-sm text-gray-600">{t("salesOrders.quantity")}: {summary}</div>
                </div>
              );
            })()}

            <div className="divide-y">
              {(itemsModalOrder.items || itemsModalOrder.sales_order_items || itemsModalOrder.order_items || []).map((item, idx) => {
                const inv = item.inventory_item || item.inventory || item.item || null;
                const name = inv?.item_name || inv?.name || item.item_name || item.name || t('common.unknown');
                const unit = inv?.unit || item.unit || "";
                const qty = parseFloat(item.quantity || item.qty || item.quantity_ordered || 0);
                return (
                  <div key={idx} className="py-2 flex items-center justify-between">
                    <div className="text-sm text-gray-900">{name}</div>
                    <div className="text-sm text-gray-700">{isNaN(qty) ? '-' : qty.toFixed(2)} {unit}</div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="px-6 py-4 border-t flex justify-end">
            <button
              type="button"
              onClick={closeItemsModal}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {t("common.cancel")}
            </button>
          </div>
        </div>
      </div>
    )}
    </div>
  </div>
  );
};

export default SalesOrderList;
